// Трекер действий пользователя: буфер + автоотправка пачками на бэкенд.
// Пишем в POST /api/activity-logs/frontend/ (source='frontend' сервер ставит сам).
import { API_URL } from '../api';
import { getAuthToken } from '../auth';

const ENDPOINT = API_URL + '/api/activity-logs/frontend/';

const FLUSH_INTERVAL_MS = 8000;  // как часто досылать накопленное
const FLUSH_THRESHOLD   = 25;    // отправить сразу, когда столько накопилось
const MAX_BATCH         = 100;   // максимум событий за одну отправку

// ── session_id: одна «сессия» на вкладку (для сборки «пути пользователя») ──
const SESSION_KEY = 'al_session_id';
function getSessionId() {
    try {
        let sid = sessionStorage.getItem(SESSION_KEY);
        if (!sid) {
            const raw = (window.crypto && window.crypto.randomUUID)
                ? window.crypto.randomUUID()
                : (String(Date.now()) + Math.random().toString(16).slice(2));
            sid = String(raw).replace(/-/g, '').slice(0, 32);
            sessionStorage.setItem(SESSION_KEY, sid);
        }
        return sid;
    } catch (e) {
        return 'nosession';
    }
}

let buffer = [];
let timer = null;
let enabled = true;
let inited = false;
let currentPath = (typeof window !== 'undefined') ? window.location.pathname : '';

export function setTrackerPath(path) { if (path) currentPath = path; }
export function setTrackerEnabled(v) { enabled = !!v; if (!v) buffer = []; }

// Основной API: зафиксировать событие.
// action — 'frontend.click' | 'frontend.page_view' | 'frontend.<область>.<действие>'
// opts.meta — любой объект деталей (без чувствительных значений ввода!)
export function track(action, opts = {}) {
    try {
        if (!enabled || !action) return;
        buffer.push({
            action: String(action).slice(0, 128),
            title: opts.title ? String(opts.title).slice(0, 255) : '',
            module: opts.module || 'frontend',
            level: opts.level || 'info',
            status: opts.status || 'success',
            path: opts.path || currentPath,
            ts: new Date().toISOString(),
            meta: (opts.meta && typeof opts.meta === 'object') ? opts.meta : {},
        });
        if (buffer.length >= FLUSH_THRESHOLD) flush();
        else scheduleFlush();
    } catch (e) { /* телеметрия не должна ломать UI */ }
}

function scheduleFlush() {
    if (timer) return;
    timer = setTimeout(() => { timer = null; flush(); }, FLUSH_INTERVAL_MS);
}

// Отправить накопленное. Работает и на уходе со страницы (keepalive).
export function flush() {
    try {
        if (timer) { clearTimeout(timer); timer = null; }
        if (!buffer.length) return;

        const token = getAuthToken();
        if (!token) { buffer = []; return; }   // не авторизованы — не копим бесконечно

        const events = buffer.splice(0, MAX_BATCH);
        const payload = JSON.stringify({ session_id: getSessionId(), events });

        fetch(ENDPOINT, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: payload,
            keepalive: true,     // переживает закрытие вкладки / переход
            credentials: 'omit',
        }).catch(() => { /* телеметрия не критична — глушим */ });
    } catch (e) { /* ignore */ }
}

// ── Глобальный автозахват кликов ──────────────────────────────────────────
function elementLabel(el) {
    const dt = el.getAttribute('data-track');
    if (dt) return dt.slice(0, 120);
    const aria = el.getAttribute('aria-label');
    if (aria) return aria.slice(0, 120);
    const title = el.getAttribute('title');
    if (title) return title.slice(0, 120);
    const text = (el.innerText || el.textContent || '').trim().replace(/\s+/g, ' ');
    if (text) return text.slice(0, 120);
    return el.tagName ? el.tagName.toLowerCase() : 'el';
}

function closestInteractive(node) {
    let el = node;
    for (let i = 0; el && i < 6; i++) {
        if (el.nodeType === 1) {
            const tag = el.tagName;
            const role = el.getAttribute ? el.getAttribute('role') : null;
            if ((el.hasAttribute && el.hasAttribute('data-track'))
                || tag === 'BUTTON' || tag === 'A' || role === 'button') {
                return el;
            }
        }
        el = el.parentElement;
    }
    return null;
}

function onGlobalClick(e) {
    try {
        const el = closestInteractive(e.target);
        if (!el) return;
        if (el.closest && el.closest('[data-track-ignore]')) return;

        const label = elementLabel(el);
        const meta = { tag: (el.tagName || '').toLowerCase(), label };

        const href = el.getAttribute && el.getAttribute('href');
        if (href) meta.href = String(href).slice(0, 200);
        const tid = el.getAttribute && el.getAttribute('data-track-id');
        if (tid) meta.track_id = String(tid).slice(0, 64);
        const extra = el.getAttribute && el.getAttribute('data-track-meta');
        if (extra) { try { Object.assign(meta, JSON.parse(extra)); } catch (e) { /* ignore */ } }

        track('frontend.click', { title: `Клик: ${label}`, meta });
    } catch (e) { /* ignore */ }
}

// Однократная инициализация: слушатели кликов и «дослать хвост» при уходе.
export function initTracker() {
    if (inited || typeof window === 'undefined') return;
    inited = true;

    document.addEventListener('click', onGlobalClick, true); // capture — до stopPropagation

    const onHide = () => flush();
    window.addEventListener('pagehide', onHide);
    window.addEventListener('beforeunload', onHide);
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush();
    });

    // Для отладки/утилит вне React (необязательно)
    try { window.__alTrack = track; } catch (e) { /* ignore */ }
}